import type { Asset, AudioCategory, AudioFolder, WorkspaceState } from "../types";
import { GLOBAL_MEDIA_CAMPAIGN_ID } from "./defaults";
import { mediaUrl } from "./media-url";

// Keep the legacy OPFS directory so existing locally cached audio remains available after the rebrand.
export const BUNDLED_MEDIA_DIRECTORY = "jftt-bundled";
export const BUNDLED_MEDIA_MANIFEST_URL = mediaUrl("/default-media/manifest.json");

export interface BundledMediaFolder {
  id: string;
  name: string;
  category: AudioCategory;
  createdAt: number;
}

export interface BundledMediaAsset {
  id: string;
  name: string;
  category: AudioCategory;
  folderId?: string;
  mime: string;
  size: number;
  hash: string;
  file: string;
  duration?: number;
  createdAt: number;
}

export interface BundledMediaManifest {
  format: "tbltop-default-media" | "jftt-default-media";
  version: 1;
  libraryVersion: string;
  folders: BundledMediaFolder[];
  assets: BundledMediaAsset[];
}

const AUDIO_CATEGORIES = new Set<AudioCategory>(["ambience", "sfx", "music"]);
const safeFile = (value: string) => Boolean(value) && !value.includes("/") && !value.includes("\\") && !value.includes("..");

export function isBundledMediaManifest(value: unknown): value is BundledMediaManifest {
  if (!value || typeof value !== "object") return false;
  const manifest = value as Partial<BundledMediaManifest>;
  if (!["tbltop-default-media", "jftt-default-media"].includes(manifest.format ?? "") || manifest.version !== 1 || typeof manifest.libraryVersion !== "string" || !manifest.libraryVersion || !Array.isArray(manifest.folders) || !Array.isArray(manifest.assets)) return false;
  return manifest.folders.every((folder) => folder && typeof folder.id === "string" && typeof folder.name === "string" && AUDIO_CATEGORIES.has(folder.category) && Number.isFinite(folder.createdAt)) && manifest.assets.every((asset) => asset && typeof asset.id === "string" && typeof asset.name === "string" && AUDIO_CATEGORIES.has(asset.category) && typeof asset.mime === "string" && asset.mime.startsWith("audio/") && Number.isFinite(asset.size) && asset.size >= 0 && /^[a-f0-9]{64}$/i.test(asset.hash) && safeFile(asset.file) && Number.isFinite(asset.createdAt) && (asset.folderId === undefined || typeof asset.folderId === "string"));
}

function deterministicAssetId(hash: string) { return `bundled-audio-${hash.slice(0, 32).toLowerCase()}`; }

/**
 * Merges repository-bundled audio metadata without replacing user edits.
 * Existing tracks are identified by content hash, so the development library
 * that produced the bundle receives no duplicates.
 */
export function mergeBundledMediaManifest(state: WorkspaceState, manifest: BundledMediaManifest): WorkspaceState {
  if (state.settings.bundledMediaVersion === manifest.libraryVersion) return state;
  const suppressed = new Set(state.settings.suppressedBundledMediaIds ?? []);
  const folders = [...(state.settings.audioFolders ?? [])];
  const folderIds = new Map<string, string>();

  for (const source of manifest.folders) {
    const existing = folders.find((folder) => folder.id === source.id) ?? folders.find((folder) => folder.category === source.category && folder.name === source.name);
    if (existing) { folderIds.set(source.id, existing.id); continue; }
    const folder: AudioFolder = { id: source.id, name: source.name, category: source.category, createdAt: source.createdAt };
    folders.push(folder); folderIds.set(source.id, folder.id);
  }

  const hashes = new Set(state.assets.filter((asset) => asset.kind === "audio" && asset.campaignId === GLOBAL_MEDIA_CAMPAIGN_ID).map((asset) => asset.hash.toLowerCase()));
  const ids = new Set(state.assets.map((asset) => asset.id));
  const additions: Asset[] = [];
  for (const source of manifest.assets) {
    const id = deterministicAssetId(source.hash);
    if (suppressed.has(id) || hashes.has(source.hash.toLowerCase()) || ids.has(id)) continue;
    additions.push({
      id,
      campaignId: GLOBAL_MEDIA_CAMPAIGN_ID,
      name: source.name,
      kind: "audio",
      mime: source.mime,
      size: source.size,
      hash: source.hash.toLowerCase(),
      opfsPath: `${BUNDLED_MEDIA_DIRECTORY}/${source.file}`,
      duration: source.duration,
      audioCategory: source.category,
      audioFolderId: source.folderId ? folderIds.get(source.folderId) : undefined,
      createdAt: source.createdAt,
    });
    hashes.add(source.hash.toLowerCase()); ids.add(id);
  }

  return {
    ...state,
    assets: additions.length ? [...state.assets, ...additions] : state.assets,
    settings: { ...state.settings, audioFolders: folders, bundledMediaVersion: manifest.libraryVersion },
  };
}

export async function loadBundledMedia(state: WorkspaceState): Promise<WorkspaceState> {
  let response: Response;
  try { response = await fetch(BUNDLED_MEDIA_MANIFEST_URL, { cache: "no-store" }); }
  catch { return state; }
  if (response.status === 404) return state;
  if (!response.ok) throw new Error(`The bundled media manifest could not be loaded (${response.status}).`);
  const manifest: unknown = await response.json();
  if (!isBundledMediaManifest(manifest)) throw new Error("The bundled media manifest is malformed.");
  return mergeBundledMediaManifest(state, manifest);
}

export function bundledMediaAssetId(hash: string) { return deterministicAssetId(hash); }
